/**
 * useGetPotDetails
 * React Query hook for fetching pot details via the V2 API
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PotDetailsV2Response } from '@billda/api';
import { getPotDetailsV2 } from '../api/potsApi';
import { mapPotDetailsV2 } from '../mappers/mapPotDetailsV2';
import { useAuthStore } from '../../auth/stores/authStore';
import { potKeys } from './potKeys';
import type { PotDetailsDisplayModel } from '../types/potDetails.types';

export interface UseGetPotDetailsReturn {
  potDetails: PotDetailsDisplayModel | undefined;
  /** The raw response, for surfaces that read the flat rows (the ledger). */
  potResponse: PotDetailsV2Response | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch detailed information for a specific pot
 *
 * The query caches the raw response and maps on read. The breakdown ledger
 * reads the flat allocation and deduction rows straight off the response,
 * while the summary card, split preview and approval panels still want the
 * rebuilt display model — one fetch, two shapes.
 *
 * @param potId - The unique identifier of the pot to fetch details for
 * @returns Pot details data, loading state, error state, and refetch function
 */
export function useGetPotDetails(potId: string | null): UseGetPotDetailsReturn {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: potKeys.detail(potId ?? ''),
    queryFn: async ({ signal }) => {
      if (!potId) {
        throw new Error('No pot ID provided');
      }
      return getPotDetailsV2(potId, signal);
    },
    enabled: isAuthenticated && !!potId,
    staleTime: 30000, // 30 seconds
  });

  const potDetails = useMemo(
    () => (data ? mapPotDetailsV2(data) : undefined),
    [data]
  );

  return {
    potDetails,
    potResponse: data,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}
